// frontend/src/pages/Admin/SellerDetail.jsx
// Página de detalle de Account Manager con estadísticas completas

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Badge,
  Button,
  Progress,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { sellersAPI } from '../../services/api';
import LineChart from '../../components/Charts/LineChart';
import { parseDate } from '../../utils/dates';

function SellerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('quotes'); // quotes, orders, clients

  useEffect(() => {
    loadStats();
  }, [id]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await sellersAPI.getStats(id);
      setStats(response.data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      setError('Error al cargar las estadísticas del vendedor');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando estadísticas...</p>
          </div>
        </Container>
      </>
    );
  }

  if (error || !stats) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="alert alert-danger shadow mt-5" role="alert">
            <span className="alert-icon"><i className="ni ni-fat-remove"></i></span>
            <span className="alert-text">{error || 'No se pudieron cargar las estadísticas'}</span>
            <button 
              className="btn btn-sm btn-outline-danger ms-3"
              onClick={() => navigate('/admin/sellers')}
            >
              <i className="ni ni-bold-left me-2"></i>
              Volver
            </button>
          </div>
        </Container>
      </>
    );
  }

  const { seller, summary, clients, evolution, recent_quotes, recent_orders } = stats;

  // Preparar datos para la gráfica
  const chartData = evolution.map(e => ({
    date: e.month_name,
    quotes: e.quotes_total,
    orders: e.orders_total,
    amount: e.orders_amount,
  }));

  return (
    <>
      <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>

      <Container fluid className="py-4">
        {/* ============================================ */}
        {/* HEADER CON INFO DEL VENDEDOR */}
        {/* ============================================ */}
        <Row className="mb-4">
          <Col lg="12">
            <Card className="shadow border-0">
              <CardBody className="p-4">
                <Row className="align-items-center">
                  <Col lg="1">
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={() => navigate('/admin/sellers')}
                    >
                      <i className="ni ni-bold-left"></i>
                    </Button>
                  </Col>
                  <Col lg="8">
                    <div className="d-flex align-items-center">
                      {seller.profile_picture ? (
                        <img
                          src={seller.profile_picture}
                          className="avatar avatar-xl shadow"
                          alt={seller.full_name}
                        />
                      ) : (
                        <div className="avatar avatar-xl bg-gradient-primary shadow">
                          <span className="text-white text-lg font-weight-bold">
                            {seller.full_name?.charAt(0) || 'A'}
                          </span>
                        </div>
                      )}
                      <div className="ms-3">
                        <h4 className="mb-0">{seller.full_name}</h4>
                        <p className="text-sm mb-0 text-secondary">
                          <i className="ni ni-email-83 me-1"></i>
                          {seller.email}
                          {seller.phone && (
                            <>
                              <span className="mx-2">•</span>
                              <i className="ni ni-mobile-button me-1"></i>
                              {seller.phone}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </Col>
                  <Col lg="3" className="text-end">
                    <Badge color="primary" pill className="badge-lg mb-2">
                      <i className="ni ni-trophy me-1"></i>
                      Ranking: #{summary.rank} de {summary.total_sellers}
                    </Badge>
                    <br />
                    <Badge color="success" pill className="badge-lg">
                      <i className="ni ni-money-coins me-1"></i>
                      ${summary.total_sales.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                    </Badge>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* CARDS DE ESTADÍSTICAS */}
        {/* ============================================ */}
        <Row>
          {/* Card 1: Cotizaciones */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Cotizaciones
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        {summary.total_quotes}
                      </h5>
                      <p className="mb-0">
                        <span className="text-success text-sm font-weight-bolder">
                          {summary.accepted_quotes} aceptadas
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle">
                      <i className="ni ni-single-copy-04 text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 2: Tasa de Conversión */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Conversión
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        {summary.conversion_rate.toFixed(1)}%
                      </h5>
                      <p className="mb-0">
                        <span className="text-xs text-muted">
                          {summary.accepted_quotes} de {summary.total_quotes}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-success shadow-success text-center rounded-circle">
                      <i className="ni ni-chart-pie-35 text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 3: Órdenes */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Órdenes
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        {summary.total_orders}
                      </h5>
                      <p className="mb-0">
                        <span className="text-xs text-muted">
                          Procesadas
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-info shadow-info text-center rounded-circle">
                      <i className="ni ni-box-2 text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 4: Valor Promedio */}
          <Col xl="3" sm="6">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Ticket Promedio
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        ${summary.avg_order_value.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                      </h5>
                      <p className="mb-0">
                        <span className="text-xs text-muted">
                          Por orden
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-warning shadow-warning text-center rounded-circle">
                      <i className="ni ni-money-coins text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* GRÁFICA DE EVOLUCIÓN */}
        {/* ============================================ */}
        <Row className="mt-4">
          <Col lg="12">
            <Card className="shadow border-0">
              <CardHeader className="pb-0 pt-3 bg-transparent border-0">
                <h6 className="text-capitalize mb-0">
                  <i className="ni ni-chart-bar-32 me-2"></i>
                  Evolución de los Últimos 12 Meses
                </h6>
                <p className="text-sm mb-0 mt-2">
                  <i className="fa fa-arrow-up text-success me-1"></i>
                  <span className="font-weight-bold">Tendencia</span> de cotizaciones y órdenes
                </p>
              </CardHeader>
              <CardBody className="p-3">
                <div className="chart">
                  {chartData.length > 0 ? (
                    <LineChart 
                      data={chartData}
                      title="Evolución Mensual"
                    />
                  ) : (
                    <div
                      style={{ height: '300px' }}
                      className="d-flex flex-column align-items-center justify-content-center bg-gradient-light border-radius-lg"
                    >
                      <i className="ni ni-chart-bar-32 text-muted" style={{ fontSize: '3rem' }}></i>
                      <p className="text-muted mb-0 mt-3 text-center">
                        No hay datos suficientes para mostrar la gráfica
                      </p>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* TABS DE NAVEGACIÓN */}
        {/* ============================================ */}
        <Row className="mt-4">
          <Col lg="12">
            <Card className="shadow border-0">
              <CardHeader className="pb-0 border-0">
                <Row className="align-items-center">
                  <Col md="6">
                    <h6 className="mb-0">
                      <i className="ni ni-bullet-list-67 me-2"></i>
                      Detalle de Actividad
                    </h6>
                  </Col>
                  <Col md="6" className="text-end">
                    <UncontrolledDropdown group size="sm">
                      <DropdownToggle caret color="primary">
                        <i className="ni ni-bullet-list-67 me-1"></i>
                        {viewMode === 'quotes' && 'Cotizaciones'}
                        {viewMode === 'orders' && 'Órdenes'}
                        {viewMode === 'clients' && 'Clientes'}
                      </DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem onClick={() => setViewMode('quotes')}>
                          <i className="ni ni-single-copy-04 me-2"></i>
                          Cotizaciones
                        </DropdownItem>
                        <DropdownItem onClick={() => setViewMode('orders')}>
                          <i className="ni ni-box-2 me-2"></i>
                          Órdenes
                        </DropdownItem>
                        <DropdownItem onClick={() => setViewMode('clients')}>
                          <i className="ni ni-briefcase-24 me-2"></i>
                          Clientes
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody className="px-0 pt-0 pb-2">
                {/* ============================================ */}
                {/* VISTA: COTIZACIONES */}
                {/* ============================================ */}
                {viewMode === 'quotes' && (
                  <div className="table-responsive p-0">
                    <Table className="align-items-center mb-0" hover>
                      <thead>
                        <tr>
                          <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Folio
                          </th>
                          <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                            Cliente
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Monto
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Estado
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Fecha
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Orden
                          </th>
                          <th className="text-secondary opacity-7"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent_quotes.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <i className="ni ni-single-copy-04 text-muted" style={{ fontSize: '3rem' }}></i>
                              <p className="text-xs text-secondary mb-0 mt-3">
                                No hay cotizaciones
                              </p>
                            </td>
                          </tr>
                        ) : (
                          recent_quotes.map((quote) => (
                            <tr 
                              key={quote.id}
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/admin/quotes/${quote.id}`)}
                            >
                              <td>
                                <div className="d-flex px-3 py-1">
                                  <div className="icon icon-shape icon-sm bg-gradient-primary shadow text-center rounded-circle me-2">
                                    <i className="ni ni-single-copy-04 text-white text-xs"></i>
                                  </div>
                                  <div className="d-flex flex-column justify-content-center">
                                    <h6 className="mb-0 text-sm">
                                      {quote.proposal_number}
                                    </h6>
                                    <p className="text-xs text-secondary mb-0">
                                      v{quote.version}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <p className="text-sm mb-0 text-dark">
                                  {quote.client_name}
                                </p>
                              </td>
                              <td className="align-middle text-center">
                                <span className="text-dark text-sm font-weight-bold">
                                  ${quote.total.toLocaleString('es-MX', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}
                                </span>
                              </td>
                              <td className="align-middle text-center">
                                <Badge 
                                  color={
                                    quote.status === 'accepted' ? 'success' :
                                    quote.status === 'rejected' ? 'danger' :
                                    'warning'
                                  }
                                  className="badge-sm"
                                >
                                  {quote.status === 'accepted' && 'Aceptada'}
                                  {quote.status === 'rejected' && 'Rechazada'}
                                  {quote.status === 'pending' && 'Pendiente'}
                                </Badge>
                              </td>
                              <td className="align-middle text-center">
                                <span className="text-secondary text-xs font-weight-bold">
                                  {parseDate(quote.created_at).toLocaleDateString('es-MX', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </span>
                              </td>
                              <td className="align-middle text-center">
                                {quote.has_order ? (
                                  <Badge color="success" className="badge-sm">
                                    <i className="ni ni-check-bold me-1"></i>
                                    Sí
                                  </Badge>
                                ) : (
                                  <Badge color="secondary" className="badge-sm">
                                    No
                                  </Badge>
                                )}
                              </td>
                              <td className="align-middle text-center">
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/admin/quotes/${quote.id}`);
                                  }}
                                >
                                  Ver
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* ============================================ */}
                {/* VISTA: ÓRDENES */}
                {/* ============================================ */}
                {viewMode === 'orders' && (
                  <div className="table-responsive p-0">
                    <Table className="align-items-center mb-0" hover>
                      <thead>
                        <tr>
                          <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Número de Orden
                          </th>
                          <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                            Cliente
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Monto
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Fecha
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Factura
                          </th>
                          <th className="text-secondary opacity-7"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent_orders.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center py-5">
                              <i className="ni ni-box-2 text-muted" style={{ fontSize: '3rem' }}></i>
                              <p className="text-xs text-secondary mb-0 mt-3">
                                No hay órdenes
                              </p>
                            </td>
                          </tr>
                        ) : (
                          recent_orders.map((order) => (
                            <tr 
                              key={order.id}
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/admin/orders/${order.id}`)}
                            >
                              <td>
                                <div className="d-flex px-3 py-1">
                                  <div className="icon icon-shape icon-sm bg-gradient-info shadow text-center rounded-circle me-2">
                                    <i className="ni ni-box-2 text-white text-xs"></i>
                                  </div>
                                  <div className="d-flex flex-column justify-content-center">
                                    <h6 className="mb-0 text-sm">
                                      {order.order_number}
                                    </h6>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <p className="text-sm mb-0 text-dark">
                                  {order.client_name}
                                </p>
                              </td>
                              <td className="align-middle text-center">
                                <span className="text-dark text-sm font-weight-bold">
                                  ${order.total.toLocaleString('es-MX', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}
                                </span>
                              </td>
                              <td className="align-middle text-center">
                                <span className="text-secondary text-xs font-weight-bold">
                                  {parseDate(order.created_at).toLocaleDateString('es-MX', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </span>
                              </td>
                              <td className="align-middle text-center">
                                {order.has_invoice ? (
                                  <Badge color="success" className="badge-sm">
                                    <i className="ni ni-check-bold me-1"></i>
                                    Sí
                                  </Badge>
                                ) : (
                                  <Badge color="warning" className="badge-sm">
                                    Pendiente
                                  </Badge>
                                )}
                              </td>
                              <td className="align-middle text-center">
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/admin/orders/${order.id}`);
                                  }}
                                >
                                  Ver
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* ============================================ */}
                {/* VISTA: CLIENTES */}
                {/* ============================================ */}
                {viewMode === 'clients' && (
                  <div className="table-responsive p-0">
                    <Table className="align-items-center mb-0" hover>
                      <thead>
                        <tr>
                          <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Cliente
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Cotizaciones
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Órdenes
                          </th>
                          <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Conversión
                          </th>
                          <th className="text-end text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                            Ventas Totales
                          </th>
                          <th className="text-secondary opacity-7"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {clients.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center py-5">
                              <i className="ni ni-briefcase-24 text-muted" style={{ fontSize: '3rem' }}></i>
                              <p className="text-xs text-secondary mb-0 mt-3">
                                No hay clientes asignados
                              </p>
                            </td>
                          </tr>
                        ) : (
                          clients.map((client, index) => (
                            <tr 
                              key={client.id}
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/admin/clients/${client.id}`)}
                            >
                              <td>
                                <div className="d-flex px-3 py-1">
                                  <div className={`icon icon-shape icon-sm ${
                                    index === 0 ? 'bg-gradient-warning' :
                                    index === 1 ? 'bg-gradient-secondary' :
                                    'bg-gradient-info'
                                  } shadow text-center rounded-circle me-2`}>
                                    <span className="text-white text-xs font-weight-bold">
                                      {index + 1}
                                    </span>
                                  </div>
                                  <div className="d-flex flex-column justify-content-center">
                                    <h6 className="mb-0 text-sm">
                                      {client.name}
                                    </h6>
                                    <p className="text-xs text-secondary mb-0">
                                      {client.rfc || 'Sin RFC'}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="align-middle text-center">
                                <Badge color="primary" className="badge-sm">
                                  {client.total_quotes}
                                </Badge>
                              </td>
                              <td className="align-middle text-center">
                                <Badge color="info" className="badge-sm">
                                  {client.total_orders}
                                </Badge>
                              </td>
                              <td className="align-middle text-center">
                                <div className="d-flex flex-column align-items-center">
                                  <span className="text-xs font-weight-bold mb-1">
                                    {client.conversion_rate.toFixed(1)}%
                                  </span>
                                  <Progress 
                                    max="100" 
                                    value={client.conversion_rate} 
                                    className="progress-sm"
                                    style={{ width: '60px' }}
                                    color={
                                      client.conversion_rate >= 75 ? 'success' :
                                      client.conversion_rate >= 50 ? 'warning' :
                                      'danger'
                                    }
                                  />
                                </div>
                              </td>
                              <td className="align-middle text-end pe-4">
                                <span className="text-dark text-sm font-weight-bold">
                                  ${client.total_sales.toLocaleString('es-MX', {
                                    maximumFractionDigits: 0
                                  })}
                                </span>
                              </td>
                              <td className="align-middle text-center">
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/admin/clients/${client.id}`);
                                  }}
                                >
                                  Ver
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* RESUMEN DE ESTADO */}
        {/* ============================================ */}
        <Row className="mt-4">
          <Col lg="6">
            <Card className="shadow border-0">
              <CardHeader className="pb-0 border-0">
                <h6 className="mb-0">
                  <i className="ni ni-chart-pie-35 me-2"></i>
                  Estado de Cotizaciones
                </h6>
              </CardHeader>
              <CardBody className="p-3">
                {/* Aceptadas */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-xs font-weight-bold text-uppercase text-success">
                      <i className="ni ni-check-bold me-1"></i>
                      Aceptadas
                    </span>
                    <span className="text-xs font-weight-bold">
                      {summary.accepted_quotes} ({((summary.accepted_quotes / summary.total_quotes) * 100).toFixed(1)}%)
                    </span>
                  </div>
                  <Progress 
                    max={summary.total_quotes} 
                    value={summary.accepted_quotes} 
                    className="progress-sm"
                    color="success"
                  />
                </div>

                {/* Pendientes */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-xs font-weight-bold text-uppercase text-warning">
                      <i className="ni ni-time-alarm me-1"></i>
                      Pendientes
                    </span>
                    <span className="text-xs font-weight-bold">
                      {summary.pending_quotes} ({((summary.pending_quotes / summary.total_quotes) * 100).toFixed(1)}%)
                    </span>
                  </div>
                  <Progress 
                    max={summary.total_quotes} 
                    value={summary.pending_quotes} 
                    className="progress-sm"
                    color="warning"
                  />
                </div>

                {/* Rechazadas */}
                <div className="mb-0">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-xs font-weight-bold text-uppercase text-danger">
                      <i className="ni ni-fat-remove me-1"></i>
                      Rechazadas
                    </span>
                    <span className="text-xs font-weight-bold">
                      {summary.rejected_quotes} ({((summary.rejected_quotes / summary.total_quotes) * 100).toFixed(1)}%)
                    </span>
                  </div>
                  <Progress 
                    max={summary.total_quotes} 
                    value={summary.rejected_quotes} 
                    className="progress-sm"
                    color="danger"
                  />
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg="6">
            <Card className="shadow border-0">
              <CardHeader className="pb-0 border-0">
                <h6 className="mb-0">
                  <i className="ni ni-briefcase-24 me-2"></i>
                  Resumen de Clientes
                </h6>
              </CardHeader>
              <CardBody className="p-3">
                <Row>
                  <Col xs="6" className="mb-3">
                    <div className="text-center">
                      <h2 className="font-weight-bolder text-dark mb-0">
                        {summary.total_clients}
                      </h2>
                      <p className="text-xs text-muted mb-0">
                        Clientes Totales
                      </p>
                    </div>
                  </Col>
                  <Col xs="6" className="mb-3">
                    <div className="text-center">
                      <h2 className="font-weight-bolder text-success mb-0">
                        {clients.filter(c => c.total_orders > 0).length}
                      </h2>
                      <p className="text-xs text-muted mb-0">
                        Con Órdenes
                      </p>
                    </div>
                  </Col>
                  <Col xs="12">
                    <div className="text-center bg-gradient-light border-radius-lg p-3">
                      <p className="text-xs text-muted mb-1">
                        Cliente Top
                      </p>
                      {clients.length > 0 && (
                        <>
                          <h6 className="mb-0 text-dark">
                            {clients[0].name}
                          </h6>
                          <p className="text-xs text-success mb-0">
                            ${clients[0].total_sales.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                          </p>
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default SellerDetail;
